// WorkOrders.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import "./WorkOrders.css";

/* -------------------------------------------------------------------------- */
/*                                  Constants                                 */
/* -------------------------------------------------------------------------- */

const STATUS_TABS = ["All", "Complete", "Scheduled", "Unscheduled"];

const emptyData = {
  kpis: { openWorkOrders: 0, completedWorkOrders: 0 },
  rows: [],
};

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 2,
  }).format(n);
};

const getStatusTone = (status) => {
  const s = String(status || "").toUpperCase();
  if (s === "COMPLETE") return "complete";
  if (s === "SCHEDULED") return "scheduled";
  if (s === "UNSCHEDULED") return "open";
  return "neutral";
};

// Parse MM/DD/YYYY date string to a comparable Date
const parseMDY = (str) => {
  if (!str) return null;
  const parts = str.split("/");
  if (parts.length !== 3) return null;
  const d = new Date(
    Number(parts[2]),
    Number(parts[0]) - 1,
    Number(parts[1])
  );
  return Number.isNaN(d.getTime()) ? null : d;
};

// Parse YYYY-MM-DD (native date input value) to a comparable Date
const parseYMD = (str) => {
  if (!str) return null;
  const d = new Date(str + "T00:00:00");
  return Number.isNaN(d.getTime()) ? null : d;
};

const matchesSearch = (row, term) => {
  if (!term) return true;
  const lower = term.toLowerCase();
  return [
    row.workOrder,
    row.description,
    row.type,
    row.status,
    row.poNumber,
    row.property,
    row.customer,
    row.completedDate,
    row.serviceAgreement,
    row.billedAmount != null ? String(row.billedAmount) : null,
  ]
    .filter(Boolean)
    .some((v) => String(v).toLowerCase().includes(lower));
};

const matchesDateRange = (row, fromDate, toDate) => {
  if (!fromDate && !toDate) return true;
  const completed = parseMDY(row.completedDate);
  if (!completed) return !fromDate && !toDate;
  if (fromDate && completed < fromDate) return false;
  if (toDate && completed > toDate) return false;
  return true;
};

/* -------------------------------------------------------------------------- */
/*                              Reusable UI pieces                            */
/* -------------------------------------------------------------------------- */

const KpiCard = ({ label, value, accent, isLoading }) => (
  <div className={`wo-kpi-card ${accent ? `wo-kpi-card--${accent}` : ""}`}>
    <div className="wo-kpi-card__label">{label}</div>
    <div className="wo-kpi-card__value">
      {isLoading ? <span className="card-spinner" /> : value}
    </div>
  </div>
);

const CompanySelect = ({ customers, selectedCustomerId, setSelectedCustomerId, disabled }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);
  const selected = customers.find((c) => String(c.id) === String(selectedCustomerId));
  const buttonLabel = selected ? selected.name : "All Companies";
  return (
    <div ref={wrapperRef} className="wo-filter-field">
      <label className="wo-filter-field__label">Company Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className="wo-select-btn"
      >
        <span className="wo-select-btn__text">{buttonLabel}</span>
        <span className={`wo-select-btn__chevron ${open ? "is-open" : ""}`}>▾</span>
      </button>
      {open && !disabled ? (
        <div className="wo-multiselect-menu">
          <div className="wo-multiselect-list">
            <div
              className={`wo-checkbox-row${!selectedCustomerId ? " is-selected" : ""}`}
              onClick={() => { setSelectedCustomerId(""); setOpen(false); }}
            >
              <span>All Companies</span>
            </div>
            {customers.map((c) => (
              <div
                key={c.id}
                className={`wo-checkbox-row${String(selectedCustomerId) === String(c.id) ? " is-selected" : ""}`}
                onClick={() => { setSelectedCustomerId(String(c.id)); setOpen(false); }}
              >
                <span>{c.name}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const PropertyMultiSelect = ({
  properties,
  selectedPropertyIds,
  setSelectedPropertyIds,
  disabled,
}) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const selectedNames = properties
    .filter((p) => selectedPropertyIds.includes(String(p.id)))
    .map((p) => p.name);

  const buttonLabel = (() => {
    if (!selectedPropertyIds.length) return "Select values";
    if (selectedPropertyIds.length === 1) return selectedNames[0] || "1 Property";
    return `${selectedPropertyIds.length} selected`;
  })();

  const toggle = (id) => {
    setSelectedPropertyIds((curr) => {
      const s = String(id);
      return curr.includes(s) ? curr.filter((x) => x !== s) : [...curr, s];
    });
  };

  return (
    <div ref={wrapperRef} className="wo-filter-field">
      <label className="wo-filter-field__label">Property Selection</label>
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((p) => !p)}
        className={`wo-select-btn ${open ? "is-open" : ""}`}
      >
        <span className="wo-select-btn__text">{buttonLabel}</span>
        <span className={`wo-select-btn__chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      {open && !disabled ? (
        <div className="wo-multiselect-menu">
          <div className="wo-multiselect-actions">
            <button
              type="button"
              className="wo-pill-btn"
              onClick={() => setSelectedPropertyIds([])}
            >
              All
            </button>
            <button
              type="button"
              className="wo-pill-btn"
              onClick={() =>
                setSelectedPropertyIds(properties.map((p) => String(p.id)))
              }
            >
              Select All
            </button>
          </div>
          <div className="wo-multiselect-list">
            {properties.length ? (
              properties.map((p) => (
                <label key={p.id} className="wo-checkbox-row">
                  <input
                    type="checkbox"
                    checked={selectedPropertyIds.includes(String(p.id))}
                    onChange={() => toggle(p.id)}
                  />
                  <span>{p.name}</span>
                </label>
              ))
            ) : (
              <div className="wo-multiselect-empty">No properties available.</div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const DateRangeFilter = ({ label, fromDate, toDate, setFromDate, setToDate }) => {
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => {
      if (!wrapperRef.current?.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onOutside);
    return () => document.removeEventListener("mousedown", onOutside);
  }, []);

  const hasValue = fromDate || toDate;
  const displayLabel = (() => {
    if (fromDate && toDate) return `${fromDate} – ${toDate}`;
    if (fromDate) return `From ${fromDate}`;
    if (toDate) return `To ${toDate}`;
    return "Select date range";
  })();

  return (
    <div ref={wrapperRef} className="wo-filter-field">
      <label className="wo-filter-field__label">{label}</label>
      <button
        type="button"
        onClick={() => setOpen((p) => !p)}
        className={`wo-select-btn ${hasValue ? "has-value" : ""}`}
      >
        <span className="wo-date-icon">&#128197;</span>
        <span className="wo-select-btn__text">{displayLabel}</span>
        <span className={`wo-select-btn__chevron ${open ? "is-open" : ""}`}>
          ▾
        </span>
      </button>

      {open ? (
        <div className="wo-date-menu">
          <div className="wo-date-row">
            <label className="wo-date-label">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="wo-date-input"
            />
          </div>
          <div className="wo-date-row">
            <label className="wo-date-label">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="wo-date-input"
            />
          </div>
          {hasValue ? (
            <button
              type="button"
              className="wo-pill-btn"
              onClick={() => {
                setFromDate("");
                setToDate("");
              }}
            >
              Clear
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                           Horizontal bar chart                             */
/* -------------------------------------------------------------------------- */

const SkeletonRows = ({ cols, count = 6 }) =>
  Array.from({ length: count }).map((_, i) => (
    <tr key={i}>
      {Array.from({ length: cols }).map((_, j) => (
        <td key={j}><span className="skeleton-line" /></td>
      ))}
    </tr>
  ));

const TypeBarChart = ({ rows, isLoading }) => {
  const counts = useMemo(() => {
    const map = {};
    for (const row of rows) {
      const t = row.type || "Unknown";
      map[t] = (map[t] || 0) + 1;
    }
    // Sort descending by count
    return Object.entries(map).sort((a, b) => b[1] - a[1]);
  }, [rows]);

  const maxCount = counts.length ? counts[0][1] : 1;

  if (isLoading) {
    return (
      <div className="wo-bar-chart">
        {[65, 48, 32, 18, 10].map((w, i) => (
          <div key={i} className="wo-bar-row">
            <div className="wo-bar-label"><span className="skeleton-line" style={{ width: 72 }} /></div>
            <div className="wo-bar-track">
              <div className="wo-bar-bg">
                <div className="skeleton-line" style={{ width: `${w}%`, height: "100%", borderRadius: 6 }} />
              </div>
              <span className="wo-bar-count"><span className="skeleton-line" style={{ width: 24 }} /></span>
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!counts.length) {
    return (
      <div className="wo-chart-empty">No data available</div>
    );
  }

  return (
    <div className="wo-bar-chart">
      {counts.map(([type, count]) => (
        <div key={type} className="wo-bar-row">
          <div className="wo-bar-label">{type}</div>
          <div className="wo-bar-track">
            <div className="wo-bar-bg">
              <div
                className="wo-bar-fill"
                style={{ width: `${Math.max(1, (count / maxCount) * 100)}%` }}
              />
            </div>
            <span className="wo-bar-count">{count}</span>
          </div>
        </div>
      ))}
    </div>
  );
};

/* -------------------------------------------------------------------------- */
/*                               WorkOrders                                   */
/* -------------------------------------------------------------------------- */

const WorkOrders = ({ viewerEmail }) => {
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [properties, setProperties] = useState([]);

  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [selectedPropertyIds, setSelectedPropertyIds] = useState([]);

  const [filterError, setFilterError] = useState("");
  const [dataError, setDataError] = useState("");

  const [data, setData] = useState(emptyData);

  const [activeStatusTab, setActiveStatusTab] = useState("All");
  const [activeTypeTab, setActiveTypeTab] = useState("All");
  const [searchTerm, setSearchTerm] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // Load filters
  useEffect(() => {
    const load = async () => {
      try {
        setFiltersLoading(true);
        setFilterError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setFilterError("No auth token found.");
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/workorders/workorders-filters`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: viewerEmail ? { email: viewerEmail } : undefined,
          }
        );

        const d = res?.data || {};
        setCustomers(Array.isArray(d.customers) ? d.customers : []);
        setProperties(Array.isArray(d.properties) ? d.properties : []);
      } catch (err) {
        console.error("Failed to load work orders filters:", err);
        setFilterError("Failed to load filters.");
      } finally {
        setFiltersLoading(false);
      }
    };

    load();
  }, [viewerEmail]);

  const filteredProperties = useMemo(() => {
    if (!selectedCustomerId) return properties;
    return properties.filter(
      (p) => String(p.customerId ?? "") === String(selectedCustomerId)
    );
  }, [properties, selectedCustomerId]);

  // Drop selected property IDs when switching customer
  useEffect(() => {
    const allowed = new Set(filteredProperties.map((p) => String(p.id)));
    setSelectedPropertyIds((curr) => curr.filter((id) => allowed.has(id)));
  }, [filteredProperties]);

  // Load data when filters change
  useEffect(() => {
    const load = async () => {
      if (filtersLoading || filterError) return;

      try {
        setDataLoading(true);
        setDataError("");

        const token = localStorage.getItem("token");
        if (!token) {
          setDataError("No auth token found.");
          return;
        }

        const res = await axios.get(
          `${process.env.REACT_APP_API_URL}/api/workorders/workorders-data`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              ...(viewerEmail ? { email: viewerEmail } : {}),
              ...(selectedCustomerId ? { customerId: selectedCustomerId } : {}),
              ...(selectedPropertyIds.length
                ? { propertyIds: selectedPropertyIds.join(",") }
                : {}),
            },
          }
        );

        setData(res?.data || emptyData);
      } catch (err) {
        console.error("Failed to load work orders data:", err);
        setDataError("Failed to load work orders data.");
        setData(emptyData);
      } finally {
        setDataLoading(false);
      }
    };

    load();
  }, [
    filtersLoading,
    filterError,
    viewerEmail,
    selectedCustomerId,
    selectedPropertyIds,
  ]);

  const fromDateObj = useMemo(() => parseYMD(fromDate), [fromDate]);
  const toDateObj = useMemo(() => parseYMD(toDate), [toDate]);

  // Derive unique types from all rows for type tabs
  const typeOptions = useMemo(() => {
    const types = new Set();
    for (const row of data.rows || []) {
      if (row.type) types.add(row.type);
    }
    return ["All", ...Array.from(types).sort()];
  }, [data.rows]);

  // Visible rows: status tab → type tab → date filter → search
  const visibleRows = useMemo(() => {
    let rows = data.rows || [];

    if (activeStatusTab !== "All") {
      rows = rows.filter(
        (r) => String(r.status || "").toUpperCase() === activeStatusTab.toUpperCase()
      );
    }

    if (activeTypeTab !== "All") {
      rows = rows.filter(
        (r) => String(r.type || "").toUpperCase() === activeTypeTab.toUpperCase()
      );
    }

    rows = rows.filter((r) => matchesDateRange(r, fromDateObj, toDateObj));
    rows = rows.filter((r) => matchesSearch(r, searchTerm));

    return rows;
  }, [data.rows, activeStatusTab, activeTypeTab, fromDateObj, toDateObj, searchTerm]);

  const handleClearSearch = () => {
    setSelectedCustomerId("");
    setSelectedPropertyIds([]);
    setActiveStatusTab("All");
    setActiveTypeTab("All");
    setSearchTerm("");
    setFromDate("");
    setToDate("");
  };

  const isLoading = filtersLoading || dataLoading;

  return (
    <div className="wo-page">
      <div className="wo-shell">
        {/* ---- Filter bar ---- */}
        <section className="wo-toolbar">
          <div className="wo-toolbar__grid">
            {/* Company */}
            <CompanySelect
              customers={customers}
              selectedCustomerId={selectedCustomerId}
              setSelectedCustomerId={setSelectedCustomerId}
              disabled={filtersLoading || !!filterError}
            />

            {/* Property */}
            <PropertyMultiSelect
              properties={filteredProperties}
              selectedPropertyIds={selectedPropertyIds}
              setSelectedPropertyIds={setSelectedPropertyIds}
              disabled={filtersLoading || !!filterError}
            />

            {/* Date completed */}
            <DateRangeFilter
              label="Date Completed Filter"
              fromDate={fromDate}
              toDate={toDate}
              setFromDate={setFromDate}
              setToDate={setToDate}
            />

            {/* Search */}
            <div className="wo-filter-field wo-filter-field--search">
              <label className="wo-filter-field__label">Search</label>
              <input
                type="text"
                placeholder=""
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="wo-search-input"
              />
            </div>

            {/* Clear */}
            <div className="wo-toolbar__clear">
              <button
                type="button"
                onClick={handleClearSearch}
                className="wo-clear-btn"
              >
                Clear Search
              </button>
            </div>
          </div>
        </section>

        {/* ---- Feedback ---- */}

        {filterError ? (
          <div className="wo-feedback wo-feedback--error">{filterError}</div>
        ) : null}

        {dataError ? (
          <div className="wo-feedback wo-feedback--error">{dataError}</div>
        ) : null}

        {!filterError ? (
          <>
            {/* ---- Summary: bar chart + KPI cards ---- */}
            <section className="wo-summary-grid">
              <div className="wo-chart-card">
                <p className="wo-chart-card__title">Work Orders by Type</p>
                <TypeBarChart rows={data.rows || []} isLoading={isLoading} />
              </div>
              <div className="wo-kpi-stack">
                <KpiCard
                  label="Open Work Orders"
                  value={data.kpis?.openWorkOrders ?? 0}
                  accent="open"
                  isLoading={isLoading}
                />
                <KpiCard
                  label="Completed Work Orders"
                  value={data.kpis?.completedWorkOrders ?? 0}
                  accent="completed"
                  isLoading={isLoading}
                />
              </div>
            </section>


            {/* ---- Table card ---- */}
            <section className="wo-table-card">
              <div className="wo-tab-row">
                {/* Status tabs */}
                <div className="wo-tab-group">
                  <span className="wo-tab-group__label">Work Order Status</span>
                  <div className="wo-tabs">
                    {STATUS_TABS.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveStatusTab(tab)}
                        className={`wo-tab ${activeStatusTab === tab ? "is-active" : ""}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Type tabs */}
                <div className="wo-tab-group">
                  <span className="wo-tab-group__label">Work Order Type</span>
                  <div className="wo-tabs">
                    {typeOptions.map((tab) => (
                      <button
                        key={tab}
                        type="button"
                        onClick={() => setActiveTypeTab(tab)}
                        className={`wo-tab ${activeTypeTab === tab ? "is-active" : ""}`}
                      >
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="wo-table-wrap">
                {isLoading || visibleRows.length > 0 ? (
                  <table className="wo-table">
                    <thead>
                      <tr>
                        <th>Work Order</th>
                        <th>Description</th>
                        <th>Work Order Outcome</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Completed Date</th>
                        <th>PO Number</th>
                        <th>Billed Amount</th>
                        <th>Service Agreement</th>
                      </tr>
                    </thead>
                    <tbody>
                      {isLoading ? <SkeletonRows cols={9} /> : visibleRows.map((row, idx) => (
                        <tr
                          key={row.jobId || `row-${idx}`}
                          className={idx % 2 === 0 ? "" : "is-alt"}
                        >
                          <td>
                            <div className="wo-primary-cell">
                              {!row.workOrder ? (
                                "—"
                              ) : row.reportUrl ? (
                                <a
                                  href={row.reportUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="wo-primary-link"
                                >
                                  {row.workOrder}
                                </a>
                              ) : (
                                row.workOrder
                              )}
                            </div>
                          </td>
                          <td className="wo-description-cell">
                            {row.description || "—"}
                          </td>
                          <td className="wo-description-cell">
                            {row.workOrderOutcome || "—"}
                          </td>
                          <td>{row.type || "—"}</td>
                          <td>
                            <span
                              className={`wo-status-pill wo-status-pill--${getStatusTone(
                                row.status
                              )}`}
                            >
                              {row.status || "—"}
                            </span>
                          </td>
                          <td>{row.completedDate || "—"}</td>
                          <td>{row.poNumber || "—"}</td>
                          <td>{formatCurrency(row.billedAmount)}</td>
                          <td>{row.serviceAgreement || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="wo-empty-state">No work orders found</div>
                )}
              </div>
            </section>
          </>
        ) : null}
      </div>
    </div>
  );
};

export default WorkOrders;
